/**
 * The tracker: what it counts, what it refuses to count, and what it sends.
 */
import {
  MAX_PATH_LENGTH,
  NOT_FOUND_EVENT_NAME,
  PAGEVIEW_EVENT_NAME,
} from '@panel/shared/analytics';
import type { TrackerEvent } from '@panel/shared/tracker';
import { scrubPath } from '@panel/shared/utils';
import type { TrackerConfig } from './config.js';
import { isLocal, optedOut } from './env.js';
import { watchUrl } from './history.js';
import { notFound, watchLinks } from './links.js';
import { historyAlreadyWrapped, thirdPartyScriptHosts } from './neighbours.js';
import { campaign, currentPath, referrerHost, viewportClass } from './page.js';
import { cleanName, cleanProps } from './props.js';
import { send } from './transport.js';

export interface Tracker {
  /** A page view. With no argument, wherever the browser says we are. */
  pageview(path?: unknown): void;
  /** A named event with optional scalar properties. */
  event(name: unknown, props?: unknown): void;
  /** Stop watching for URL changes and put `history` back. Used by the browser suite. */
  stop(): void;
}

const NOTHING: Tracker = {
  pageview: () => undefined,
  event: () => undefined,
  stop: () => undefined,
};

/**
 * Build a tracker over a window.
 *
 * The window is a parameter rather than a global so the unit suite can hand it
 * a jsdom one and so the browser suite can inspect what was decided. The
 * shipped bundle passes the real one, once, from `global.ts`.
 *
 * ## Why this returns a tracker even when it will never send anything
 *
 * A visitor who opted out, or a developer on `localhost`, still has a page that
 * calls `panel('signup')` from a click handler. Returning something inert means
 * that call is a no-op; returning null or throwing would mean a `TypeError` in
 * the customer's own code, on their own machine, caused by a privacy signal
 * they did the right thing by honouring.
 */
export function createTracker(win: Window, config: TrackerConfig): Tracker {
  const doc = win.document;
  const loc = win.location;

  // Decided once, at construction. Re-reading Do Not Track on every event would
  // let a single-page app that changes it mid-session produce a half-counted
  // session, which is worse than either answer.
  const counting = !optedOut(win) && (config.countLocal || !isLocal(loc));
  if (!counting) return NOTHING;

  // Measured BEFORE `watchUrl` runs, or the answer is "yes, by us".
  const wrappedFirstBySomeoneElse = config.diagnostics && historyAlreadyWrapped(win);

  let lastPath: string | null = null;
  let diagnosticsSent = false;

  const dispatch = (
    name: string,
    path: string,
    props?: unknown,
    destination?: string,
  ): void => {
    const event: TrackerEvent = { k: config.key, n: name, u: path };

    // Where an outbound click or a download went. `u` stays the page the click
    // happened ON, which is the half a customer acts on: "they leave for
    // Instagram from the product page" is a decision about the product page.
    if (destination) event.d = destination;

    const referrer = referrerHost(doc, loc);
    if (referrer) event.r = referrer;

    const viewport = viewportClass(win);
    if (viewport) event.v = viewport;

    // Read on every dispatch rather than once at construction: a single-page
    // application navigating away from the landing URL changes the query
    // string, and a campaign that stayed attached to every later event would
    // credit the campaign with the whole visit's page views instead of with
    // the arrival. The rollup attributes a session from its FIRST event, so
    // the arrival is the one that counts either way — but sending the truth
    // per event is what keeps that the rollup's decision and not this file's.
    const utm = config.utm && campaign(loc);
    if (utm) event.c = utm;

    const clean = cleanProps(props);
    if (clean) event.p = clean;

    // Once per page load, on the first thing we send. Not on every event: it is
    // the same list every time, and repeating it would put a customer's script
    // inventory on the wire on every click of every visitor for no new fact.
    if (config.diagnostics && !diagnosticsSent) {
      diagnosticsSent = true;
      const hosts = thirdPartyScriptHosts(doc, loc);
      if (hosts.length) event.s = hosts;
      if (wrappedFirstBySomeoneElse) event.x = 1;
    }

    send(win, config.endpoint, JSON.stringify(event));
  };

  const pageview = (raw?: unknown): void => {
    // A caller-supplied path goes through the same redaction as one read from
    // the address bar. It is the escape hatch for hash routers (see below), so
    // it is exactly the entry point most likely to be handed a URL with an
    // identifier in it.
    const path =
      typeof raw === 'string' && raw
        ? scrubPath(raw, MAX_PATH_LENGTH) || '/'
        : currentPath(loc);

    // The same page twice in a row is not two page views, and the two ways it
    // happens are both routine rather than exotic. A router that pushes and
    // then replaces produces one URL change and two calls; React's StrictMode
    // invokes every effect twice in development, so an app reporting page views
    // from a `useEffect` calls this twice for one navigation on every
    // developer's machine and once in production. Comparing the SCRUBBED path
    // also means a `replaceState` that only rewrites the query string — which
    // is how a filter panel or a map keeps its state in the URL, firing dozens
    // of times a minute — is correctly not a navigation, because the query
    // string is not part of what we send.
    if (path === lastPath) return;
    lastPath = path;
    dispatch(PAGEVIEW_EVENT_NAME, path);
  };

  const event = (rawName: unknown, props?: unknown): void => {
    const name = cleanName(rawName);
    if (!name) return;
    // Named events are never deduplicated: two identical purchases are two
    // purchases, and the caller is explicit in a way a URL change is not.
    dispatch(name, currentPath(loc), props);
  };

  let unwatch: (() => void) | undefined;
  let unwatchLinks: (() => void) | undefined;

  if (config.links) {
    // Attached before the first page view is sent, and deliberately not inside
    // the `config.auto` branch below. A site that turns the automatic page view
    // off — a single-page application reporting its own routes — still has
    // links leaving it, and losing the outbound table for that customer would
    // be an accident of how two unrelated switches happened to nest.
    unwatchLinks = watchLinks(win, (hit) =>
      dispatch(hit.name, currentPath(loc), undefined, hit.destination),
    );
  }

  if (config.auto) {
    /**
     * Hash routes are NOT followed, and this is a refusal rather than an
     * omission.
     *
     * `#/cart` is a real route in an application built on a hash router, and
     * counting it would mean sending the fragment — which is the half of a URL
     * this script drops on purpose, because it is where a page puts state it
     * never expected to leave the browser. An allow-list of "safe" fragments is
     * the same unwinnable guess as an allow-list of safe query parameters.
     *
     * So a hash-routed application reports its own routes, explicitly, with
     * `panel.pageview('/cart')`. The customer chooses what leaves their page,
     * we redact it on the way out and again on arrival, and nothing is inferred
     * from a fragment by default. It is more work for a shrinking minority of
     * applications and it is the only version of this that is honest.
     */
    unwatch = watchUrl(win, () => pageview());

    const start = (): void => {
      pageview();
      // After the page view and never instead of it: a 404 page IS a page that
      // was served and read, and a total that quietly excluded them would
      // disagree with the customer's own server logs by exactly the number this
      // feature exists to show them. `config.links` gates it because it is the
      // same decision — see `TrackerConfig.links`.
      //
      // Inside the `auto` branch, and so NOT sent by a site that turns the
      // automatic page view off. That is not an oversight and it costs almost
      // nothing: the status being read is the one that delivered this DOCUMENT,
      // and a site reporting its own routes is a single-page application whose
      // "not found" is a soft 404 its server answered 200 for. There would be
      // nothing here to read. A server-rendered site that turns `auto` off is
      // the case this does lose, and it is rare enough to be worth one sentence
      // in the README rather than a second attribute.
      if (config.links && notFound(win)) {
        dispatch(NOT_FOUND_EVENT_NAME, currentPath(loc));
      }
    };
    // A prerendered page is one Chrome loaded in the background because it
    // guessed the visitor might go there. Counting it inflates every prepared
    // page and invents visits that never happened; waiting for activation costs
    // nothing on the pages that were not prerendered, because the flag is
    // false and this branch never runs.
    const prerendering = (doc as Document & { prerendering?: boolean }).prerendering;
    if (prerendering) {
      doc.addEventListener('prerenderingchange', start, { once: true });
    } else {
      start();
    }
  }

  return {
    pageview,
    event,
    stop: () => {
      if (unwatch) unwatch();
      unwatch = undefined;
      if (unwatchLinks) unwatchLinks();
      unwatchLinks = undefined;
    },
  };
}
